import type { ReactNode } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

/**
 * List-page layout for surfaces inset inside a parent (tab, drawer, modal body, etc.).
 * Uses the same slot order as {@link PageFullWidthLayout}: banner → header + actions → toolbar →
 * scrollable content (a shadow at an edge scrolled past), without viewport height, dockable
 * header, hamburger, or footer outlet.
 *
 * Pair with {@link PageFullWidthLayout} for the same entity-list chrome on a root route vs inside
 * another page.
 */
export interface PageInsetListLayoutProps {
  /**
   * Optional strip above the title row. Omit (or pass `false` / `null`) when unused.
   * Wrapper uses `empty:hidden` so components that render nothing take no space.
   */
  banner?: ReactNode;
  header: ReactNode;
  actions: ReactNode;
  /** Filters / secondary controls. Omit when the inset page has no toolbar row (e.g. simple notes). */
  toolbar?: ReactNode;
  content: ReactNode;
  className?: string;
  /** Classes of the scroll area around `content`. */
  contentClassName?: string;
}

export function PageInsetListLayout({
  banner,
  header,
  actions,
  toolbar,
  content,
  className,
  contentClassName
}: PageInsetListLayoutProps) {
  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 flex-1 flex-col bg-bg-default',
        className
      )}
    >
      {banner != null && banner !== false ? (
        <div className="shrink-0 empty:hidden">{banner}</div>
      ) : null}
      <div className="shrink-0 flex items-center justify-between p-6">
        <div className="flex-1 text-xl font-semibold">{header}</div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      {toolbar != null && toolbar !== false ? (
        <div className="shrink-0 px-6 pb-2">{toolbar}</div>
      ) : null}
      <ScrollShadowWrapper
        edge="shadow"
        className="min-h-0 flex-1 with-styled-scrollbars with-styled-scrollbars__transparent"
      >
        {/* the padding sits inside, so the edge shadows span the whole width */}
        <div className={cn('p-6', contentClassName)}>{content}</div>
      </ScrollShadowWrapper>
    </div>
  );
}
